import {
  PortalConfig,
  ContentResponse,
  CacheEntry,
  CareersContent,
  CareerApplicationRequest,
  CareerApplicationResponse,
  DhanurvedaPaymentOrderRequest,
  DhanurvedaPaymentOrderResponse,
  DhanurvedaPaymentVerifyRequest,
  DhanurvedaPaymentVerifyResponse,
  CurriculumDeckResponse,
  WallFeedResponse,
} from './types';
import { normalizeApiBaseUrl } from './apiBase';

const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL ?? '');
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, CacheEntry<any>>();

// Simple cache management
function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  
  const now = Date.now();
  if (now - entry.timestamp > entry.ttl) {
    cache.delete(key);
    return null;
  }
  
  return entry.data as T;
}

function setCached<T>(key: string, data: T, ttl = CACHE_TTL): void {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  });
}

// Fetch with fallback to cache
async function fetchWithCache<T>(
  url: string,
  cacheKey: string,
  options: RequestInit = {}
): Promise<T> {
  // Check cache first
  const cached = getCached<T>(cacheKey);
  if (cached) {
    console.log(`[API] Using cached data for ${cacheKey}`);
    return cached;
  }

  try {
    console.log(`[API] Fetching ${url}`);
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    setCached(cacheKey, data);
    return data;
  } catch (error) {
    console.error(`[API] Error fetching ${url}:`, error);
    
    // Try to return cached data as fallback
    const fallback = getCached<T>(cacheKey);
    if (fallback) {
      console.log(`[API] Using stale cache as fallback for ${cacheKey}`);
      return fallback;
    }
    
    throw error;
  }
}

export function apiPath(path: string): string {
  return `${API_BASE_URL}${path}`;
}

/**
 * Fetch portal configuration from backend
 */
export async function getPortalConfig(locale = 'hi'): Promise<PortalConfig> {
  return fetchWithCache<PortalConfig>(
    apiPath(`/api/config?locale=${encodeURIComponent(locale)}`),
    `portal-config-${locale}`,
  );
}

/**
 * Fetch curriculum content file
 */
export async function getContent(filename: string): Promise<string> {
  const response = await fetchWithCache<ContentResponse>(
    apiPath(`/api/content/${filename}`),
    `content-${filename}`,
  );
  
  if (response.error) {
    throw new Error(`Content error: ${response.error}`);
  }
  
  return response.markdown;
}

export async function getCurriculumDeck(filename: string): Promise<CurriculumDeckResponse> {
  const response = await fetch(apiPath(`/api/content/${filename}/deck`), {
    cache: 'no-store',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = typeof body.detail === 'string' ? body.detail : `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(detail);
  }

  return response.json() as Promise<CurriculumDeckResponse>;
}

/**
 * Fetch careers content from backend
 */
export async function getCareers(locale = 'hi'): Promise<CareersContent> {
  return fetchWithCache<CareersContent>(
    apiPath(`/api/careers?locale=${encodeURIComponent(locale)}`),
    `careers-${locale}`,
  );
}

export async function submitCareerApplication(
  payload: CareerApplicationRequest,
): Promise<CareerApplicationResponse> {
  const formData = new FormData();
  formData.append('jobId', payload.jobId);
  formData.append('locale', payload.locale ?? 'hi');
  formData.append('fullName', payload.fullName);
  formData.append('email', payload.email);
  formData.append('phone', payload.phone);
  formData.append('currentLocation', payload.currentLocation);
  formData.append('yearsExperience', String(payload.yearsExperience));
  formData.append('availableFrom', payload.availableFrom);
  formData.append('motivation', payload.motivation);
  formData.append('willingToRelocate', String(payload.willingToRelocate));
  formData.append('consentToContact', String(payload.consentToContact));
  if (payload.portfolioUrl) {
    formData.append('portfolioUrl', payload.portfolioUrl);
  }
  if (payload.referrer) {
    formData.append('referrer', payload.referrer);
  }
  formData.append('resumeFile', payload.resumeFile, payload.resumeFile.name);
  if (payload.coverLetterFile) {
    formData.append('coverLetterFile', payload.coverLetterFile, payload.coverLetterFile.name);
  }

  const candidatePaths = ['/api/careers/applications', '/api/careers'];
  let lastError: Error | null = null;

  for (const path of candidatePaths) {
    const response = await fetch(apiPath(path), {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      return response.json() as Promise<CareerApplicationResponse>;
    }

    const errorBody = await response.json().catch(() => ({}));
    const detail = typeof errorBody.detail === 'string'
      ? errorBody.detail
      : `HTTP ${response.status}: ${response.statusText}`;

    if (response.status !== 404 && response.status !== 405) {
      throw new Error(detail);
    }

    lastError = new Error(detail);
  }

  throw lastError ?? new Error('Career application submission failed.');
}

export async function createDhanurvedaPaymentOrder(
  payload: DhanurvedaPaymentOrderRequest,
): Promise<DhanurvedaPaymentOrderResponse> {
  const requestBody = {
    booking_ref: payload.bookingRef,
    batch_id: payload.batchId,
    batch_name: payload.batchName,
    batch_dates: payload.batchDates,
    full_name: payload.fullName,
    email: payload.email,
    phone: payload.phone,
    city: payload.city,
    emergency_name: payload.emergencyName,
    emergency_phone: payload.emergencyPhone,
    billing_country: payload.billingCountry,
    advance_amount: payload.advanceAmount,
    uid: payload.uid,
    medical_notes: payload.medicalNotes,
    age_confirmed: payload.ageConfirmed,
    health_declaration: payload.healthDeclaration,
    risk_accepted: payload.riskAccepted,
    terms_accepted: payload.termsAccepted,
  };

  const response = await fetch(apiPath('/api/dhanurveda/payments/orders'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const detail = typeof errorBody.detail === 'string'
      ? errorBody.detail
      : `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(detail);
  }

  return response.json() as Promise<DhanurvedaPaymentOrderResponse>;
}

export async function verifyDhanurvedaPayment(
  payload: DhanurvedaPaymentVerifyRequest,
): Promise<DhanurvedaPaymentVerifyResponse> {
  const response = await fetch(apiPath('/api/dhanurveda/payments/verify'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      order_id: payload.orderId,
      payment_id: payload.paymentId,
      signature: payload.signature,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const detail = typeof errorBody.detail === 'string'
      ? errorBody.detail
      : `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(detail);
  }

  return response.json() as Promise<DhanurvedaPaymentVerifyResponse>;
}

export async function fetchWallFeed(idToken: string, days = 9): Promise<WallFeedResponse> {
  const response = await fetch(apiPath(`/api/wall-feed?days=${days}`), {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const detail = typeof errorBody.detail === 'string'
      ? errorBody.detail
      : `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(detail);
  }

  return response.json() as Promise<WallFeedResponse>;
}

/**
 * Clear all cache
 */
export function clearCache(): void {
  cache.clear();
  console.log('[API] Cache cleared');
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; entries: string[] } {
  return {
    size: cache.size,
    entries: Array.from(cache.keys()),
  };
}

export default {
  getPortalConfig,
  getContent,
  getCareers,
  submitCareerApplication,
  createDhanurvedaPaymentOrder,
  verifyDhanurvedaPayment,
  getSangatiMembers,
  getSangatiThreads,
  createSangatiThread,
  getSangatiThread,
  postSangatiThreadMessage,
  askSangatiAssistant,
  getSangatiHandshakes,
  createSangatiHandshake,
  approveSangatiHandshake,
  searchLinguistics,
  clearCache,
  getCacheStats,
};

// ── Discovery / Matching API ────────────────────────────────────────────────

export interface PublicProfile {
  uid: string;
  alias: string;
  profileKind: string;
  age?: number;
  rating?: number;
  learningLevel: string;
  nativeLanguage: string;
  preferredScript: string;
  timeZone: string;
  pincode: string;
  studyAreas: string[];
  qualification: string;
  sessionModes: string[];
  availability: string[];
  collaborationMode: string;
  lookingFor: string;
  /** Gateway governance role (shiksharthi|upadhyaya|acharya|guru|raja) — used for server-side gating */
  gatewayRole?: string;
  activeRole?: string;
}

export type VisAudience = 'public' | 'self' | 'emails' | 'aliases';

export interface ProfileVisibilitySettings {
  realName:      VisAudience;
  age:           VisAudience;
  pincode:       VisAudience;
  timeZone:      VisAudience;
  qualification: VisAudience;
  rating:        VisAudience;
  studyAreas:    VisAudience;
  availability:  VisAudience;
  sessionModes:  VisAudience;
  lookingFor:    VisAudience;
}

export const DEFAULT_VISIBILITY: ProfileVisibilitySettings = {
  realName:      'self',
  age:           'self',
  pincode:       'self',
  timeZone:      'self',
  qualification: 'public',
  rating:        'public',
  studyAreas:    'public',
  availability:  'public',
  sessionModes:  'public',
  lookingFor:    'public',
};

/** Profile as returned by GET /api/profiles/view/:uid — fields omitted based on viewer context. */
export interface FilteredProfile {
  uid: string;
  alias: string;
  profileKind: string;
  learningLevel: string;
  nativeLanguage: string;
  preferredScript: string;
  collaborationMode: string;
  isDiscoverable?: boolean;
  createdAt?: string;
  updatedAt?: string;
  fieldStates: Record<string, 'visible' | 'hidden' | 'empty'>;
  // Conditionally present (visibility-dependent)
  realName?: string;
  age?: number;
  pincode?: string;
  timeZone?: string;
  qualification?: string;
  rating?: number;
  studyAreas?: string[];
  availability?: string[];
  sessionModes?: string[];
  lookingFor?: string;
}

export interface AliasLookupResult {
  uid: string;
  alias: string;
  profileKind: string;
  learningLevel: string;
  nativeLanguage: string;
  collaborationMode: string;
}

export interface MatchResult extends PublicProfile {
  score: number;
  reasons: string[];
}

export interface InviteRedeemResult {
  uid: string;
  profile_snapshot: PublicProfile;
  expires_at: string;
}

export interface SangatiMember extends PublicProfile {
  isDiscoverable?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SangatiThreadSummary {
  id: string;
  type: 'group' | 'dm';
  title: string;
  participant_uids: string[];
  participant_count: number;
  message_count: number;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  last_message_preview: string;
  last_message_sender_alias: string;
}

export interface SangatiMessage {
  id: string;
  thread_id: string;
  sender_uid: string;
  sender_alias: string;
  body: string;
  created_at: string;
}

export interface SangatiThreadDetail {
  thread: SangatiThreadSummary;
  messages: SangatiMessage[];
}

export interface SangatiThreadCreateRequest {
  actor_uid: string;
  actor_alias: string;
  type: 'group' | 'dm';
  title?: string;
  participant_uids: string[];
}

export interface SangatiThreadMessageRequest {
  actor_uid: string;
  actor_alias: string;
  body: string;
}

export interface SangatiAssistantRequest {
  question: string;
  actor_uid?: string;
  actor_alias?: string;
  feature?: string;
}

export interface SangatiAssistantResponse {
  answer: string;
  provider: string;
  suggestions: string[];
}

export interface SangatiHandshake {
  code: string;
  requester_uid: string;
  requester_alias: string;
  requester_email: string;
  lookup_code: string;
  target_uid: string;
  target_alias: string;
  invitee_email: string;
  status: 'pending' | 'approved' | 'declined';
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  approved_by_uid: string | null;
  approved_by_alias: string | null;
}

export interface SangatiHandshakeCreateRequest {
  requester_uid: string;
  requester_alias: string;
  requester_email: string;
  lookup_code: string;
  target_uid: string;
  target_alias: string;
  invitee_email: string;
}

export interface SangatiHandshakeApproveRequest {
  approver_uid: string;
  approver_alias: string;
  approver_email: string;
}

export interface LinguisticMatch {
  source: string;
  path: string;
  term: string;
  title: string;
  transliteration?: string;
  meaning?: string;
  root_structure: string;
  hindi_meaning: string;
  nirukti_explanation: string;
  summary: string;
  root_source?: string;
  context_count?: number;
  contexts?: VocabularyContext[];
  score: number;
}

export interface VocabularyContext {
  id: string;
  title: string;
  description?: string;
  examples?: string[];
  notes?: Record<string, string> | string[];
  tags?: string[];
}

export interface LinguisticSearchResult {
  query: string;
  count: number;
  sources: { source: string; count: number }[];
  matches: LinguisticMatch[];
}

async function apiCall(url: string, options: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers as Record<string,string> },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw Object.assign(new Error(body.detail ?? res.statusText), { status: res.status });
  }
  return res;
}

export async function syncPublicProfile(
  uid: string,
  profile: PublicProfile,
  visibility?: { settings: ProfileVisibilitySettings; allowedEmails: string[]; allowedAliases: string[]; realName?: string },
  idToken?: string,
): Promise<void> {
  const body: Record<string, unknown> = { ...profile };
  if (visibility) {
    body.visibility      = visibility.settings;
    body.allowedEmails   = visibility.allowedEmails;
    body.allowedAliases  = visibility.allowedAliases;
    if (visibility.realName !== undefined) body.realName = visibility.realName;
  }
  const extraHeaders: Record<string, string> = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  await apiCall(apiPath(`/api/profiles/${uid}`), {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: extraHeaders,
  });
}

export async function removePublicProfile(uid: string, idToken?: string): Promise<void> {
  const extraHeaders: Record<string, string> = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  await apiCall(apiPath(`/api/profiles/${uid}`), { method: 'DELETE', headers: extraHeaders });
}

export async function getProfileView(uid: string, idToken?: string): Promise<FilteredProfile> {
  const extraHeaders: Record<string, string> = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res  = await apiCall(apiPath(`/api/profiles/view/${uid}`), { headers: extraHeaders });
  const data = await res.json();
  return data.profile as FilteredProfile;
}

export async function getProfileByAlias(alias: string): Promise<AliasLookupResult> {
  const res = await apiCall(apiPath(`/api/profiles/by-alias/${encodeURIComponent(alias)}`));
  return res.json() as Promise<AliasLookupResult>;
}

export async function getMatches(uid: string): Promise<MatchResult[]> {
  const res = await apiCall(apiPath(`/api/profiles/matches/${uid}`));
  const data = await res.json();
  return data.matches as MatchResult[];
}

export async function createInvite(uid: string, profileSnapshot: PublicProfile): Promise<string> {
  const res = await apiCall(apiPath('/api/invites'), {
    method: 'POST',
    body: JSON.stringify({ uid, profile_snapshot: profileSnapshot }),
  });
  const data = await res.json();
  return data.code as string;
}

export async function redeemInviteCode(code: string): Promise<InviteRedeemResult> {
  const res = await apiCall(apiPath(`/api/invites/${encodeURIComponent(code.toUpperCase().trim())}`));
  return res.json();
}

export async function getSangatiMembers(): Promise<SangatiMember[]> {
  const res = await apiCall(apiPath('/api/sangati/members'));
  const data = await res.json();
  return data.members as SangatiMember[];
}

export async function getSangatiThreads(uid: string): Promise<SangatiThreadSummary[]> {
  const res = await apiCall(apiPath(`/api/sangati/chats?uid=${encodeURIComponent(uid)}`));
  const data = await res.json();
  return data.threads as SangatiThreadSummary[];
}

export async function createSangatiThread(payload: SangatiThreadCreateRequest): Promise<SangatiThreadSummary> {
  const res = await apiCall(apiPath('/api/sangati/chats/threads'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.thread as SangatiThreadSummary;
}

export async function getSangatiThread(threadId: string, uid: string): Promise<SangatiThreadDetail> {
  const res = await apiCall(apiPath(`/api/sangati/chats/threads/${encodeURIComponent(threadId)}?uid=${encodeURIComponent(uid)}`));
  return res.json() as Promise<SangatiThreadDetail>;
}

export async function postSangatiThreadMessage(threadId: string, payload: SangatiThreadMessageRequest): Promise<SangatiThreadDetail> {
  const res = await apiCall(apiPath(`/api/sangati/chats/threads/${encodeURIComponent(threadId)}/messages`), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<SangatiThreadDetail>;
}

export async function askSangatiAssistant(payload: SangatiAssistantRequest): Promise<SangatiAssistantResponse> {
  const res = await apiCall(apiPath('/api/sangati/assistant'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<SangatiAssistantResponse>;
}

export async function getSangatiHandshakes(email: string): Promise<SangatiHandshake[]> {
  const res = await apiCall(apiPath(`/api/sangati/handshakes?email=${encodeURIComponent(email)}`));
  const data = await res.json();
  return data.requests as SangatiHandshake[];
}

export async function createSangatiHandshake(payload: SangatiHandshakeCreateRequest): Promise<SangatiHandshake> {
  const res = await apiCall(apiPath('/api/sangati/handshakes'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.handshake as SangatiHandshake;
}

export async function approveSangatiHandshake(code: string, payload: SangatiHandshakeApproveRequest): Promise<SangatiHandshake> {
  const res = await apiCall(apiPath(`/api/sangati/handshakes/${encodeURIComponent(code)}/approve`), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.handshake as SangatiHandshake;
}

// ── Versioned Conversations ──────────────────────────────────────────────────

export interface ConvBlock {
  id: string;
  sanskrit_text: string;
  hindi_translation: string;
  vyakarana_nirukta: string;
  speaker_expression: string;
  context: string;
}

export interface ConvSummary {
  id: string;
  title: string;
  snippet: string;
  block_count: number;
  version: number;
  privacy: 'private' | 'shared';
  shared_with: { uid: string }[];
  created_at: string;
  updated_at: string;
}

export interface ConvFull extends ConvSummary {
  owner_uid: string;
  blocks: ConvBlock[];
  versions: { version: number; title: string; blocks: ConvBlock[]; updated_at: string }[];
}

async function convAuthCall(url: string, idToken: string, options: RequestInit = {}): Promise<Response> {
  // Build headers first, then spread only non-header options, so Content-Type is never overridden.
  const { headers: callerHeaders, ...rest } = options;
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...(callerHeaders as Record<string, string>),
    },
    ...rest,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw Object.assign(new Error(body.detail ?? res.statusText), { status: res.status });
  }
  return res;
}

export async function listConversations(idToken: string): Promise<ConvSummary[]> {
  const res = await convAuthCall(apiPath('/api/studio/conversations'), idToken);
  const data = await res.json();
  return data.conversations as ConvSummary[];
}

export async function getConversation(id: string, idToken: string): Promise<ConvFull> {
  const res = await convAuthCall(apiPath(`/api/studio/conversations/${encodeURIComponent(id)}`), idToken);
  return res.json() as Promise<ConvFull>;
}

export async function createConversation(
  payload: { title: string; privacy: string; blocks: Partial<ConvBlock>[] },
  idToken: string,
): Promise<ConvSummary> {
  const res = await convAuthCall(apiPath('/api/studio/conversations'), idToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.conversation as ConvSummary;
}

export async function updateConversation(
  id: string,
  payload: { title: string; privacy: string; blocks: Partial<ConvBlock>[]; client_version: number },
  idToken: string,
): Promise<ConvSummary> {
  const res = await convAuthCall(apiPath(`/api/studio/conversations/${encodeURIComponent(id)}`), idToken, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.conversation as ConvSummary;
}

export async function deleteConversation(id: string, idToken: string): Promise<void> {
  await convAuthCall(apiPath(`/api/studio/conversations/${encodeURIComponent(id)}`), idToken, { method: 'DELETE' });
}

export async function shareConversation(id: string, recipientUid: string, idToken: string): Promise<ConvSummary> {
  const res = await convAuthCall(apiPath(`/api/studio/conversations/${encodeURIComponent(id)}/share`), idToken, {
    method: 'POST',
    body: JSON.stringify({ recipient_uid: recipientUid }),
  });
  const data = await res.json();
  return data.conversation as ConvSummary;
}

export async function searchLinguistics(query: string, limit = 12): Promise<LinguisticSearchResult> {
  const safeQuery = query.trim();
  const res = await fetch(apiPath(`/api/linguistics/search?q=${encodeURIComponent(safeQuery)}&limit=${limit}`), {
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw Object.assign(new Error(body.detail ?? res.statusText), { status: res.status });
  }

  return res.json();
}
